import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Role } from 'src/app/@core/constants';
import { Branch } from 'src/app/models/branch';
import { Menu } from 'src/app/models/menu';
import { SalesOrg } from 'src/app/models/sales-org';
import { User } from 'src/app/models/user';
import { AuthService } from 'src/app/services/auth.service';
import { CaptchaService } from 'src/app/services/captcha.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { ExceptionSolutionService } from 'src/app/services/exception-solution.service';
import { ParamsService } from 'src/app/services/params.service';
import { PhysicalEstablishmentService } from 'src/app/services/physical-establishment.service';
import { RegisterService } from 'src/app/services/register.service';
import { UsersService } from 'src/app/services/users.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-dashboard',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.sass'],
})
export class LoginComponent implements OnInit {

  form: FormGroup = new FormGroup({
    username: new FormControl("", [Validators.required]),
    password: new FormControl("", [Validators.required]),
    captcha: new FormControl("", [Validators.required]),
    salesOrg: new FormControl("", [Validators.required]),
    branch: new FormControl("", [Validators.required]),
    captchaId: new FormControl("")
  });
  public salesOrgs: Array<SalesOrg> = [];
  public branches: any = {};
  public filteredBranches: Array<Branch> = [];
  constructor(
    private authService: AuthService,
    private router: Router,
    private configurationService: ConfigurationService,
    private captchaService: CaptchaService,
    private readonly exceptionSolutionService: ExceptionSolutionService,
    private readonly paramsService: ParamsService,
    private readonly registerService: RegisterService,
    private readonly physicalEsService: PhysicalEstablishmentService,
    private readonly userService: UsersService,
  ) { }

  ngOnInit(): void {
    this.loadConfiguration();
    if (this.hideCaptcha) {
      this.form.get("captcha")?.clearValidators();
    }
  }

  loadConfiguration() {
    this.configurationService.loadBranchesFromPrevSettings();
    this.salesOrgs = this.configurationService.readSalesOrg();
    this.branches = this.configurationService.readBranch();
    if (this.configurationService.readSelectedBranchAndSalesOrg()?.salesOrg?.code && this.configurationService.readSelectedBranchAndSalesOrg()?.branch?.code) {
      this.form.get("salesOrg")?.setValue(this.configurationService.readSelectedBranchAndSalesOrg().salesOrg.code);
      this.form.get("branch")?.setValue(this.configurationService.readSelectedBranchAndSalesOrg().branch);
      this.filteredBranches = this.branches[this.form.value.salesOrg] || [];
    }
    this.configurationService.showMenu();
  }

  async onSubmit(): Promise<void> {
    const { username, password, captcha, captchaId } = this.form.value;

    if (!this.hideCaptcha) {
      const captchaResult = await this.captchaService.readIsValid(captchaId, captcha, "LoginSalesforce").toPromise();
      if (!captchaResult.response) {
        this.exceptionSolutionService.showAlertWithSolution(`Inicio de Sesión`, `Captcha Incorrecto`);
        return;
      }
    }
    let token = (await this.authService
      .readToken(username, password)
      .toPromise().catch((error: HttpErrorResponse) => {
        if (error.status == 403) {
          this.goToChangePassword(username);
        }
      })) as string;
    let user = await this.authService.readProfileFromAccessToken(token);
    if (this.automaticConfig(token)) {
      return;
    }
    if ((this.form.valid) || (this.form.invalid && [...user.roles].some(s => s == Role.ROLE_ADMIN))) {
      this.authService.saveTokenInStorage(token);
      if (!await this.validateIsActiveLogin(user)) {
        Swal.fire({
          title: `¡Atención!`,
          text: "El ingreso manual al sistema esta deshabilitado para este usuario, por favor iniciar sesión por medio de Salesforce",
          icon: "warning",
          confirmButtonText: "Aceptar",
        });
        this.authService.removeTokenFromStorage();
        return;
      }

      this.authService.saveProfileInStorage(user);
      this.configurationService.createSelectedBranchAndSalesOrg({
        salesOrg: this.salesOrgs.find((f) => f.code == this.form.value.salesOrg),
        branch: this.form.value.branch,
      });
      await this.authService.savePrivilegesInStorage();
      await this.authService.saveAccessInStorage();
      await this.authService.saveMenusInStorage();


      await this.configurationService.configureFromCookies();

      const menus: Array<Menu> = Array.from(JSON.parse(localStorage.getItem('menus') || JSON.stringify({})));

      if (menus.length > 0) {
        const menu = menus.filter(f => f.salesOrg.includes(this.form.value.salesOrg))[0] ?? menus[0]
        this.router.navigateByUrl(menu.redirectTo);
      } else {
        this.router.navigateByUrl('');
      }
    }
    else {
      this.exceptionSolutionService.showAlertWithSolution(`Inicio de Sesión`, `Seleccione una tienda valida para continuar`);
    }
  }
  automaticConfig(token: string) {
    if (this.configurationService.readSalesOrg()?.length > 0) {
      return false;
    }

    this.router.navigate(["autologin"], {
      queryParams: {
        access_token: token
      }
    });

    return true;
  }
  async validateIsActiveLogin(user: User) {
    const activeToUseParam = await this.paramsService.readParam("active-to-use-outside-sf").toPromise();

    if ((activeToUseParam.response?.value ?? "").toLowerCase() != "no") {
      return true;
    }

    return !user.salesforceLicense;
  }

  filterBranches() {
    if (this.form.get("salesOrg")?.invalid || !this.form.get("salesOrg")?.value) {
      this.filteredBranches = [];
    }
    this.filteredBranches = this.branches[this.form.value.salesOrg];
    this.form.get("branch")?.reset();
  }

  goToChangePassword(user?: string) {
    this.router.navigate(["changepassword", user || ""])
  }

  goToChangeForgetPassword() {
    this.router.navigate(["changeforgetpassword"])
  }

  getCaptchaId(id: string) {
    this.form.patchValue({
      captchaId: id
    });
  }

  customSearchBranches(term: string, item: any) {
    term = term.toLocaleLowerCase();
    return item['name'].toLocaleLowerCase().indexOf(term) > -1 || item['code'].toString().indexOf(term) > -1;
  }

  showNotConfigured() {
    return !this.branches || !this.salesOrgs || this.salesOrgs.length == 0;
  }

  get hideCaptcha() {
    return this.configurationService.readSigninWithoutCaptcha();

  }

}


